import type { StatusTone } from "@/components/ui";
import type {
  AudienceAction,
  AudienceStatus,
  NotificationAction,
  NotificationDeliveryAction,
  NotificationDeliveryStatus,
  NotificationStatus,
  NotificationTemplateAction,
  NotificationTemplateStatus,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function notificationStatusTone(status: NotificationStatus): StatusTone {
  switch (status) {
    case "draft":
      return "info";
    case "pending_approval":
      return "warning";
    case "scheduled":
      return "info";
    case "sent":
    case "delivered":
      return "success";
    case "failed":
      return "danger";
    case "cancelled":
    case "archived":
      return "neutral";
    default:
      return "neutral";
  }
}

export function templateStatusTone(
  status: NotificationTemplateStatus,
): StatusTone {
  switch (status) {
    case "draft":
      return "info";
    case "pending_approval":
      return "warning";
    case "approved":
      return "success";
    case "retired":
      return "neutral";
    default:
      return "neutral";
  }
}

export function audienceStatusTone(status: AudienceStatus): StatusTone {
  switch (status) {
    case "draft":
      return "info";
    case "approved":
    case "updated":
      return "warning";
    case "active":
      return "success";
    case "retired":
      return "neutral";
    default:
      return "neutral";
  }
}

export function deliveryStatusTone(
  status: NotificationDeliveryStatus,
): StatusTone {
  switch (status) {
    case "pending":
    case "retrying":
      return "warning";
    case "sent":
    case "delivered":
      return "success";
    case "failed":
      return "danger";
    case "cancelled":
    case "suppressed":
      return "neutral";
    default:
      return "neutral";
  }
}

export const TEMPLATE_ACTIONS_FROM: Record<
  NotificationTemplateAction,
  readonly NotificationTemplateStatus[]
> = {
  submit_approval: ["draft"],
  approve: ["pending_approval"],
  retire: ["draft", "pending_approval", "approved"],
};

export const AUDIENCE_ACTIONS_FROM: Record<
  AudienceAction,
  readonly AudienceStatus[]
> = {
  approve: ["draft"],
  activate: ["approved", "updated"],
  mark_updated: ["active", "approved"],
  retire: ["draft", "approved", "active", "updated"],
};

export const NOTIFICATION_ACTIONS_FROM: Record<
  NotificationAction,
  readonly NotificationStatus[]
> = {
  submit_approval: ["draft"],
  approve: ["pending_approval"],
  schedule: ["pending_approval", "draft"],
  mark_sent: ["scheduled"],
  mark_delivered: ["sent"],
  fail: ["draft", "pending_approval", "scheduled", "sent"],
  cancel: ["draft", "pending_approval", "scheduled"],
  archive: [
    "draft",
    "pending_approval",
    "scheduled",
    "sent",
    "delivered",
    "failed",
    "cancelled",
  ],
};

export const DELIVERY_ACTIONS_FROM: Record<
  NotificationDeliveryAction,
  readonly NotificationDeliveryStatus[]
> = {
  mark_sent: ["pending", "retrying"],
  mark_delivered: ["sent"],
  mark_failed: ["pending", "sent", "retrying"],
  schedule_retry: ["failed"],
  cancel: ["pending", "sent", "failed", "retrying"],
  suppress: ["pending", "retrying", "failed"],
};

/** Governance-safe campaign actions (no transmit). */
export const CAMPAIGN_GOVERNANCE_ACTIONS: NotificationAction[] = [
  "submit_approval",
  "approve",
  "schedule",
  "cancel",
  "archive",
];

/** Status remediation only — does not call delivery worker. */
export const CAMPAIGN_REMEDIATION_ACTIONS: NotificationAction[] = [
  "mark_sent",
  "mark_delivered",
  "fail",
];

export function allowedTemplateActions(
  status: NotificationTemplateStatus,
): NotificationTemplateAction[] {
  return (
    Object.keys(TEMPLATE_ACTIONS_FROM) as NotificationTemplateAction[]
  ).filter((action) => TEMPLATE_ACTIONS_FROM[action].includes(status));
}

export function allowedAudienceActions(
  status: AudienceStatus,
): AudienceAction[] {
  return (Object.keys(AUDIENCE_ACTIONS_FROM) as AudienceAction[]).filter(
    (action) => AUDIENCE_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedNotificationActions(
  status: NotificationStatus,
): NotificationAction[] {
  return (Object.keys(NOTIFICATION_ACTIONS_FROM) as NotificationAction[]).filter(
    (action) => NOTIFICATION_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedDeliveryActions(
  status: NotificationDeliveryStatus,
): NotificationDeliveryAction[] {
  return (
    Object.keys(DELIVERY_ACTIONS_FROM) as NotificationDeliveryAction[]
  ).filter((action) => DELIVERY_ACTIONS_FROM[action].includes(status));
}

export function toIsoUtc(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toISOString();
}
